import React, { useState, useEffect, useCallback } from 'react'
import { Row, Col, Table, Button, Modal, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import MaskedInput from 'react-text-mask'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'

import PacienteService from '../../../services/paciente.service'
import { tiposTelefone } from '../../../constants/telefones.constants'

const MFA_CODIGO_MINUTOS_VALIDADE = 20

function maskTelefoneBr(rawValue) {
  const digits = String(rawValue || '').replace(/\D/g, '')
  if (digits.length <= 10) {
    return ['(', /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
  }
  return ['(', /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
}

const TIPO_LABEL = {
  [tiposTelefone.PESSOAL]: 'Pessoal',
  [tiposTelefone.TRABALHO]: 'Trabalho',
  [tiposTelefone.OUTROS]: 'Outros',
  [tiposTelefone.WHATSAPP]: 'WhatsApp',
}

function labelTipo(v) {
  if (v === undefined || v === null) return '—'
  return TIPO_LABEL[v] || '—'
}

function formatPhoneBr(numero) {
  if (numero == null || numero === '') return '—'
  const d = String(numero).replace(/\D/g, '')
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return String(numero)
}

function onlyDigits(s) {
  return String(s || '').replace(/\D/g, '')
}

function TelefonesPacienteSection({ telefones, pacienteId, canEdit, onRefresh, showModal, onCloseModal }) {
  const [tipoTelefone, setTipoTelefone] = useState(tiposTelefone.PESSOAL)
  const [principal, setPrincipal] = useState(true)
  const [telefoneInput, setTelefoneInput] = useState('')
  const [codigo, setCodigo] = useState('')
  const [mfaGerado, setMfaGerado] = useState(false)
  const [loadingGerar, setLoadingGerar] = useState(false)
  const [loadingConfirmar, setLoadingConfirmar] = useState(false)
  const [loadingSalvarDireto, setLoadingSalvarDireto] = useState(false)
  const [telefoneDigitsGerado, setTelefoneDigitsGerado] = useState('')
  const [savingPrincipalId, setSavingPrincipalId] = useState(null)

  const resetModalFields = useCallback(() => {
    setTelefoneInput('')
    setCodigo('')
    setMfaGerado(false)
    setTelefoneDigitsGerado('')
    setLoadingGerar(false)
    setLoadingConfirmar(false)
    setLoadingSalvarDireto(false)
  }, [])

  const list = telefones || []
  const ehPrimeiroTelefone = list.length === 0

  useEffect(() => {
    if (!showModal) return
    resetModalFields()
    setTipoTelefone(tiposTelefone.PESSOAL)
    setPrincipal(ehPrimeiroTelefone)
  }, [showModal, resetModalFields, ehPrimeiroTelefone])

  const closeModal = () => {
    if (onCloseModal) onCloseModal()
  }

  const onTipoChange = (e) => {
    const v = Number(e.target.value)
    setTipoTelefone(v)
    if (v !== tiposTelefone.WHATSAPP) {
      setMfaGerado(false)
      setCodigo('')
      setTelefoneDigitsGerado('')
    }
  }

  const handleSalvarDireto = async () => {
    const digits = onlyDigits(telefoneInput)
    if (digits.length < 10 || digits.length > 11) {
      toast('Informe um telefone válido (DDD + número).', { type: toast.TYPE.WARNING })
      return
    }
    if (!pacienteId) {
      toast('Salve o paciente antes de adicionar telefone.', { type: toast.TYPE.WARNING })
      return
    }
    if (tipoTelefone === tiposTelefone.WHATSAPP) {
      toast('Para WhatsApp use a validação MFA abaixo.', { type: toast.TYPE.WARNING })
      return
    }
    setLoadingSalvarDireto(true)
    try {
      await PacienteService.incluirTelefonePaciente(pacienteId, {
        numero: digits,
        tipoTelefone,
        principal: ehPrimeiroTelefone || principal,
      })
      toast('Telefone salvo.', { type: toast.TYPE.SUCCESS })
      closeModal()
      if (onRefresh) await onRefresh()
    } catch (e) {
    } finally {
      setLoadingSalvarDireto(false)
    }
  }

  const handleGerarMfa = async () => {
    if (tipoTelefone !== tiposTelefone.WHATSAPP) return
    const digits = onlyDigits(telefoneInput)
    if (digits.length < 10 || digits.length > 11) {
      toast('Informe um celular válido (DDD + número).', { type: toast.TYPE.WARNING })
      return
    }
    if (!pacienteId) {
      toast('Salve o paciente antes de adicionar telefone.', { type: toast.TYPE.WARNING })
      return
    }
    setLoadingGerar(true)
    try {
      await PacienteService.gerarMfa({
        telefone: digits,
        pacienteId,
      })
      setTelefoneDigitsGerado(digits)
      setMfaGerado(true)
      toast('Código enviado. Informe o recebido no WhatsApp.', { type: toast.TYPE.SUCCESS })
    } catch (e) {
    } finally {
      setLoadingGerar(false)
    }
  }

  const handleConfirmar = async () => {
    const c = onlyDigits(codigo)
    if (c.length !== 6) {
      toast('Informe o código de 6 dígitos.', { type: toast.TYPE.WARNING })
      return
    }
    if (!telefoneDigitsGerado) {
      toast('Gere o código antes de confirmar.', { type: toast.TYPE.WARNING })
      return
    }
    setLoadingConfirmar(true)
    try {
      await PacienteService.confirmarTelefoneComMfa(pacienteId, {
        telefone: telefoneDigitsGerado,
        codigo: c,
        pacienteId,
        principal: ehPrimeiroTelefone || principal,
      })
      toast('Telefone WhatsApp vinculado com sucesso.', { type: toast.TYPE.SUCCESS })
      closeModal()
      if (onRefresh) await onRefresh()
    } catch (e) {
    } finally {
      setLoadingConfirmar(false)
    }
  }

  const handlePrincipalRow = async (tel, checked) => {
    if (!pacienteId || !tel.id || tel.principal === checked) return
    setSavingPrincipalId(tel.id)
    try {
      await PacienteService.definirPrincipalTelefonePaciente(pacienteId, tel.id, checked)
      toast(checked ? 'Telefone definido como principal.' : 'Principal atualizado.', { type: toast.TYPE.SUCCESS })
      if (onRefresh) await onRefresh()
    } catch (e) {
    } finally {
      setSavingPrincipalId(null)
    }
  }

  const temPeloMenosUmPrincipal = list.some((t) => t.principal)
  const podeRemoverTelefones = list.length > 1 && temPeloMenosUmPrincipal

  const motivoBloqueioRemover = !podeRemoverTelefones
    ? list.length <= 1
      ? 'É necessário ter mais de um telefone cadastrado para remover.'
      : 'Defina um telefone como principal antes de remover.'
    : null

  const MSG_BLOQUEIO_PRINCIPAL =
    'Não é possível remover o telefone principal. Defina outro número como principal antes.'

  const podeRemoverLinha = (tel) => podeRemoverTelefones && !tel.principal

  const tooltipRemoverLinha = (tel) => {
    if (!podeRemoverTelefones) {
      return motivoBloqueioRemover || 'Remover telefone'
    }
    if (tel.principal) {
      return MSG_BLOQUEIO_PRINCIPAL
    }
    return 'Remover telefone'
  }

  const handleRemover = async (tel) => {
    if (!pacienteId || !tel.id) return
    if (!podeRemoverTelefones) {
      toast(motivoBloqueioRemover || 'Não é possível remover este telefone.', { type: toast.TYPE.WARNING })
      return
    }
    if (tel.principal) {
      toast(MSG_BLOQUEIO_PRINCIPAL, { type: toast.TYPE.WARNING })
      return
    }
    const result = await Swal.fire({
      title: 'Remover este telefone?',
      html: `<span>${formatPhoneBr(tel.numero)}</span>`,
      icon: 'warning',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    })
    if (result.isConfirmed !== true && result.value !== true) return
    try {
      await PacienteService.removerTelefonePaciente(pacienteId, tel.id)
      toast('Telefone removido.', { type: toast.TYPE.SUCCESS })
      if (onRefresh) await onRefresh()
    } catch (e) {
    }
  }

  const isWhatsApp = tipoTelefone === tiposTelefone.WHATSAPP

  return (
    <Row className="mt-3 pt-2">
      <Col>
        <div className="mb-2">
          <h2 className="paciente-form-section-title mb-0">Telefone / WhatsApp</h2>
        </div>
        <p className="text-muted small mb-2">
          Use o botão flutuante <strong>Adicionar telefone</strong> (ícone de telefone).{' '}
          <strong>WhatsApp</strong> exige MFA (código no aparelho); os demais tipos salvam na hora.
        </p>

        {list.length > 0 ? (
          <Table responsive size="sm" bordered hover className="mb-0 bg-white">
            <thead className="thead-light">
              <tr>
                <th>Número</th>
                <th className="text-center">Principal</th>
                <th>Tipo</th>
                <th className="text-center" style={{ width: 56 }}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id || t.numero}>
                  <td>{formatPhoneBr(t.numero)}</td>
                  <td className="text-center align-middle">
                    {canEdit && pacienteId && t.id ? (
                      <Form.Check
                        type="checkbox"
                        checked={!!t.principal}
                        disabled={savingPrincipalId === t.id}
                        onChange={(e) => handlePrincipalRow(t, e.target.checked)}
                        aria-label="Telefone principal"
                        className="d-inline-block"
                      />
                    ) : (
                      <span>{t.principal ? 'Sim' : '—'}</span>
                    )}
                  </td>
                  <td>{labelTipo(t.tipoTelefone)}</td>
                  <td className="text-center align-middle">
                    {canEdit && pacienteId && t.id && (
                      <OverlayTrigger
                        placement="left"
                        overlay={
                          <Tooltip id={`tel-rem-${t.id}`}>{tooltipRemoverLinha(t)}</Tooltip>
                        }
                      >
                        <span className="d-inline-block">
                          <Button
                            type="button"
                            variant="link"
                            className="text-danger p-1 border-0"
                            onClick={() => handleRemover(t)}
                            aria-label="Remover telefone"
                            disabled={!podeRemoverLinha(t)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </span>
                      </OverlayTrigger>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted small mb-0">Nenhum telefone cadastrado.</p>
        )}

        <Modal
          show={!!showModal}
          onHide={closeModal}
          centered
          backdrop="static"
          dialogClassName="paciente-form-item-modal__dialog paciente-form-item-modal__dialog--compact"
          className="paciente-form-item-modal paciente-form-telefone-modal"
        >
          <Modal.Header closeButton className="py-2 pl-3 pr-2 border-bottom-0">
            <div>
              <Modal.Title as="h6" className="mb-0 font-weight-bold text-dark">
                Novo telefone
              </Modal.Title>
              <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
                {isWhatsApp
                  ? mfaGerado
                    ? `Digite o código de 6 dígitos (válido por ${MFA_CODIGO_MINUTOS_VALIDADE} min após o envio).`
                    : 'Toque em Enviar código para receber o MFA no WhatsApp.'
                  : 'Salva na hora, sem validação por WhatsApp.'}
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="paciente-form-telefone-modal-body pt-0 px-3 pb-2">
            <Form.Group className="mb-2">
              <Row className="no-gutters">
                <Col xs={7} className="pr-2">
                  <Form.Label className="small text-muted mb-1 d-block">Tipo</Form.Label>
                </Col>
                <Col xs={5}>
                  <Form.Label
                    htmlFor="novo-tel-principal"
                    className="small text-muted mb-1 d-block"
                  >
                    Principal
                  </Form.Label>
                </Col>
              </Row>
              <Row className="no-gutters align-items-center">
                <Col xs={7} className="pr-2">
                  <Form.Control
                    size="sm"
                    as="select"
                    value={tipoTelefone}
                    onChange={onTipoChange}
                    disabled={mfaGerado}
                  >
                    <option value={tiposTelefone.PESSOAL}>Pessoal</option>
                    <option value={tiposTelefone.TRABALHO}>Trabalho</option>
                    <option value={tiposTelefone.OUTROS}>Outros</option>
                    <option value={tiposTelefone.WHATSAPP}>WhatsApp</option>
                  </Form.Control>
                </Col>
                <Col xs={5} className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="novo-tel-principal"
                    className="mb-0"
                    style={{ marginTop: 0 }}
                    checked={ehPrimeiroTelefone || principal}
                    onChange={(e) => setPrincipal(e.target.checked)}
                    disabled={ehPrimeiroTelefone || (mfaGerado && isWhatsApp)}
                    title={ehPrimeiroTelefone ? 'O primeiro telefone é sempre principal.' : undefined}
                  />
                </Col>
              </Row>
            </Form.Group>

            {isWhatsApp && !mfaGerado && (
              <div className="rounded border border-info bg-light mb-2 px-2 py-2">
                <div className="font-weight-bold text-dark mb-1" style={{ fontSize: '0.8rem' }}>
                  Validação WhatsApp (MFA)
                </div>
                <ul className="mb-0 pl-3 text-muted" style={{ fontSize: '0.72rem', lineHeight: 1.45 }}>
                  <li>
                    Informe o <strong>celular com DDD</strong> e toque em <strong>Enviar código</strong>.
                  </li>
                  <li>
                    Você receberá uma mensagem no <strong>WhatsApp</strong> com o código de 6 dígitos.
                  </li>
                  <li>
                    O código <strong>expira em {MFA_CODIGO_MINUTOS_VALIDADE} minutos</strong> após o envio; se
                    expirar, solicite um novo.
                  </li>
                  <li>Na próxima etapa, digite o código para concluir o vínculo.</li>
                </ul>
              </div>
            )}

            <Form.Group className="mb-2">
              <Form.Label className="small text-muted mb-1">Número (DDD + número)</Form.Label>
              <MaskedInput
                mask={maskTelefoneBr}
                guide
                placeholder="(00) 00000-0000"
                value={telefoneInput}
                onChange={(e) => setTelefoneInput(e.target.value)}
                disabled={(isWhatsApp && mfaGerado) || loadingSalvarDireto}
                autoComplete="tel"
                className="form-control form-control-sm"
                type="tel"
                inputMode="numeric"
              />
            </Form.Group>

            {isWhatsApp && mfaGerado && (
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted mb-1">Código MFA (6 dígitos)</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  disabled={loadingConfirmar}
                  className="text-center"
                  style={{ letterSpacing: '0.35em', fontWeight: 600 }}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2 px-3 border-top d-flex justify-content-between align-items-center flex-wrap bg-white">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="text-secondary p-0 m-0"
              onClick={closeModal}
            >
              Cancelar
            </Button>
            <div className="d-flex align-items-center" style={{ gap: '0.35rem' }}>
              {!isWhatsApp && (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={handleSalvarDireto}
                  disabled={loadingSalvarDireto}
                >
                  {loadingSalvarDireto ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Salvando
                    </>
                  ) : (
                    'Salvar'
                  )}
                </Button>
              )}
              {isWhatsApp && !mfaGerado && (
                <Button type="button" size="sm" variant="primary" onClick={handleGerarMfa} disabled={loadingGerar}>
                  {loadingGerar ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Enviando
                    </>
                  ) : (
                    'Enviar código'
                  )}
                </Button>
              )}
              {isWhatsApp && mfaGerado && (
                <Button
                  type="button"
                  size="sm"
                  variant="success"
                  onClick={handleConfirmar}
                  disabled={loadingConfirmar}
                >
                  {loadingConfirmar ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Confirmando
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </Button>
              )}
            </div>
          </Modal.Footer>
        </Modal>
      </Col>
    </Row>
  )
}

export default TelefonesPacienteSection
